import {
  HTMLProps,
  useCallback,
  useMemo,
} from 'react';

import {
  LocalVideoTrack,
  RemoteUser,
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
} from 'agora-rtc-react';

import MicrophoneIndicator from '@/app/components/MicrophoneIndicator';
import NoVideoIndicator from '@/app/components/NoVideoIndicator';
import VideoDisplayTitle from '@/app/components/VideoDisplayTitle';
import { useParticipantData } from '@/app/queries/MeetingQuery/context';
import { useAppContext } from '../AppContext';

export interface VideoTrackInfo {
  uid: number,
  source: ICameraVideoTrack | IAgoraRTCRemoteUser,
  sourceType: 'local' | 'remote',
  isEnlarged: boolean,
  hasAudio: boolean,
  hasVideo: boolean,
}

export type VideoDisplayVariant = 'stage' | 'tile';

// On a phone the stage runs edge to edge under the floating header, so its
// microphone badge is pulled clear of it and the name is dropped — a
// full-screen view needs no label. On a desktop the stage is a panel of its
// own and both badges sit in its corners, as on any tile.
const BADGE_PLACEMENT: Record<VideoDisplayVariant, { title: string, microphone: string }> = {
  stage: { title: 'hidden md:block md:m-3 md:max-w-xs', microphone: 'top-20 md:top-3' },
  tile: { title: 'inset-x-1 bottom-1 text-xs', microphone: '' },
};

interface VideoDisplayProps extends Omit<HTMLProps<HTMLDivElement>, 'onClick'> {
  videoTrack: VideoTrackInfo;
  onClick(videoTrack: VideoTrackInfo): void;
  variant?: VideoDisplayVariant;
}

export default function VideoDisplay({
  onClick, videoTrack, variant = 'tile', ...divProps
}: VideoDisplayProps) {
  const { speaker, isMainVideoMirrored } = useAppContext();
  const participantData = useParticipantData(videoTrack.uid);

  const onClickTrack = useCallback(() => {
    onClick(videoTrack);
  }, [onClick, videoTrack]);

  // A remote stage is letterboxed: cropping it would cut away part of the
  // property the customer is showing. Everything else fills its box, the way a
  // camera viewfinder does.
  const playerConfig = useMemo(() => ({
    fit: variant === 'stage' && videoTrack.sourceType === 'remote'
      ? 'contain' as const
      : 'cover' as const,
    mirror: videoTrack.sourceType === 'local' && isMainVideoMirrored,
  }), [variant, videoTrack.sourceType, isMainVideoMirrored]);

  const badges = BADGE_PLACEMENT[variant];

  switch (videoTrack.sourceType) {
    case 'local':
      return (
        <>
          <VideoDisplayTitle className={badges.title} title={participantData?.name} />
          <LocalVideoTrack
            {...divProps}
            track={videoTrack.source as ICameraVideoTrack}
            play={videoTrack.hasVideo}
            videoPlayerConfig={playerConfig}
            onClick={onClickTrack}
          >
            <MicrophoneIndicator className={badges.microphone} isMicrophoneOn={videoTrack.hasAudio} />
            <NoVideoIndicator isCameraOn={videoTrack.hasVideo} />
          </LocalVideoTrack>
        </>

      )
    case 'remote':
      return (
        <>
          {!participantData?.isScreenShare && (
            <VideoDisplayTitle className={badges.title} title={participantData?.name} />
          )}
          <RemoteUser
            {...divProps}
            playbackDeviceId={speaker.deviceId}
            user={videoTrack.source as IAgoraRTCRemoteUser}
            playVideo={true}
            playAudio={true}
            videoPlayerConfig={playerConfig}
            onClick={onClickTrack}
          >
            <MicrophoneIndicator className={badges.microphone} isMicrophoneOn={videoTrack.hasAudio} />
            <NoVideoIndicator isCameraOn={videoTrack.hasVideo} />
          </RemoteUser>
        </>
      );
    default: {
      const neverType: never = videoTrack.sourceType;
      throw new TypeError(`Unhandled video type: ${neverType}`);
    }
  }
}
